import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react'
import { SupabaseClient, User, Provider } from '@supabase/supabase-js'
import { supabase } from '../services/supabaseClient'
import { ensureUserAccount } from '../services/accountService'
import { trackLoginEvent } from '../services/loginTracker'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  supabase: SupabaseClient
  signIn: (email: string, password: string) => Promise<void>
  signInWithProvider: (provider: Provider) => Promise<void>
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>
  signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSettingUpAccount, setIsSettingUpAccount] = useState(false)
  const setupInProgressRef = useRef(false)
  
  useEffect(() => {
    // Simple check for session
    const checkSession = async () => {
      try {
        console.log('Checking for active session');
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error checking session:', error);
          setUser(null);
        } else if (data.session) {
          console.log('Active session found for user:', data.session.user.email);
          setUser(data.session.user);
        } else {
          console.log('No active session found');
          setUser(null);
        }
      } catch (err) {
        console.error('Exception during session check:', err);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event);
      
      // Only update user state for specific events to avoid multiple renders
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' || event === 'PASSWORD_RECOVERY') {
        setUser(session?.user || null);
      }
      
      // If this is a new sign-in (but not password recovery), set up the user account
      if (event === 'SIGNED_IN' && session && !setupInProgressRef.current) {
        // Track the login event
        trackLoginEvent(session.user.id);
        
        // Check if user has a pending invitation in various places
        const pendingInvitation = sessionStorage.getItem('pendingInvitation');
        const urlParams = new URLSearchParams(window.location.search);
        const isInvitationFlow = urlParams.get('invitation') || window.location.pathname.includes('/invitation/');
        
        // Check if user came from an invitation email (has invitation data in metadata)
        const userMetadata = session.user.user_metadata || {};
        const hasInvitationData = userMetadata.invitation_token || userMetadata.type === 'team_invitation';
        const shouldSkipAccountCreation = userMetadata.skip_account_creation === true;
        
        if (pendingInvitation || isInvitationFlow || hasInvitationData || shouldSkipAccountCreation) {
          console.log('User is in invitation flow, skipping automatic account setup', {
            pendingInvitation,
            isInvitationFlow,
            hasInvitationData,
            shouldSkipAccountCreation,
            metadata: userMetadata
          });
          
          // If we have invitation token in metadata, redirect to accept it
          if (userMetadata.invitation_token && !window.location.pathname.includes('/invitation/')) {
            window.location.href = `/invitation/${userMetadata.invitation_token}`;
          }
        } else {
          console.log('User signed in, setting up account');
          setupUserAccount(session);
        }
      }
      
      // Don't set up account during password recovery
      if (event === 'PASSWORD_RECOVERY') {
        console.log('Password recovery mode - skipping account setup');
      }
    });
    
    // Helper function to set up user account
    const setupUserAccount = async (session: any) => {
      // Prevent duplicate setup calls
      if (setupInProgressRef.current) {
        console.log('Account setup already in progress, skipping');
        return;
      }
      
      try {
        setupInProgressRef.current = true;
        setIsSettingUpAccount(true);
        console.log('Setting up account for user:', session.user.email);
        
        // Use the direct account service instead of calling Edge Function
        // This avoids CORS issues in production
        const success = await ensureUserAccount(session);
        
        if (success) {
          console.log('Account setup successful');
        } else {
          console.warn('Account setup failed, will retry on dashboard');
        }
      } catch (err) {
        console.error('Exception during account setup:', err);
      } finally {
        setupInProgressRef.current = false;
        setIsSettingUpAccount(false);
      }
    };
    
    // Initial session check
    checkSession();
    
    // Return cleanup function
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }
  
  const signInWithProvider = async (provider: Provider): Promise<void> => {
    console.log('Initiating OAuth sign-in with provider:', provider)
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });
    
    if (error) {
      console.error('OAuth sign-in error:', error);
      throw error;
    }
    
    if (!data?.url) {
      console.error('No redirect URL provided!');
      throw new Error('No redirect URL received from authentication provider');
    }
    
    // Redirect to the provider's authentication page
    window.location.href = data.url;
  }
  
  const signUp = async (email: string, password: string, metadata?: Record<string, any>) => {
    try {
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: metadata
        }
      })
      if (error) throw error
      return data
    } catch (error) {
      console.error('Sign up error:', error)
      throw error
    }
  }
  
  const signOut = async () => {
    // First, clear local user state to immediately update the UI
    setUser(null)
    
    // Then sign out from Supabase
    await supabase.auth.signOut({ scope: 'local' })
  }
  
  const value = {
    user,
    isLoading,
    supabase,
    signIn,
    signInWithProvider,
    signUp,
    signOut
  }
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}